import { Pencil } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  SiteLocationDto,
  SiteLocationStatusDto,
  SiteLocationStatusKind,
} from '@/services/siteLocationService'

import './SiteLocationTable.css'

type Props = {
  /** Column header for the name — "Yard", "Block". */
  header: string
  locations: SiteLocationDto[]
  /** This type's statuses, so a row can show its status's name and kind. */
  statuses: SiteLocationStatusDto[]
  onEdit: (location: SiteLocationDto) => void
}

const STATUS_CLASS_NAME: Record<SiteLocationStatusKind, string> = {
  Planned: 'site-location-table__status--planned',
  Active: 'site-location-table__status--active',
  Retired: 'site-location-table__status--retired',
}

function SiteLocationTable({ header, locations, statuses, onEdit }: Props) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="site-location-table__head">{header}</TableHead>
          <TableHead className="site-location-table__head">Status</TableHead>
          <TableHead className="site-location-table__head text-right">
            <span className="sr-only">Edit</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {locations.map((location) => {
          const status = statuses.find((s) => s.id === location.statusId)
          return (
            <TableRow key={location.id}>
              <TableCell className="py-3 font-bold text-(--fg1)">
                {location.name}
              </TableCell>
              <TableCell className="py-3">
                {status ? (
                  <Badge
                    variant="secondary"
                    className={`site-location-table__status ${STATUS_CLASS_NAME[status.kind]}`}
                  >
                    <span
                      className="site-location-table__status-dot"
                      aria-hidden="true"
                    />
                    {status.name.toUpperCase()}
                  </Badge>
                ) : null}
              </TableCell>
              <TableCell className="py-3 text-right">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Edit ${location.name}`}
                  onClick={() => onEdit(location)}
                >
                  <Pencil aria-hidden="true" />
                </Button>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default SiteLocationTable
